import React, { useCallback, useEffect, useRef } from "react";
import { TableBody, TablePagination } from "@mui/material";

import {
	CircularLoader,
	CommonFilters,
	CustomButton,
	CustomTable,
	CustomTableCell,
	CustomTableHead,
	CustomTableRow,
	FiltersWrapper,
	icon,
	ProjectCategoriesFilter,
	ListWithRequest,
	SidePanel,
	SidePanelContent,
} from "../../common/components";

import { translate } from "../../common/providers";
import styles from "./DeliverablesList.module.css";
import { exportView } from "../../navigation";
import ProjectPhaseFilter from "../../common/components/filters5/filters/project-phases/ProjectPhaseFilter";
import HeaderDeliverableRow from "./components/header-deliverable-row/HeaderDeliverableRow";
import DeliverableRow from "./components/deliverable-row/DeliverableRow";
import DeliverableSidePanel from "./components/deliverable-side-panel/DeliverableSidePanel";

import {
	useDeliverablesFilters,
	useDeliverablesData,
	useDialogOptions,
	useSidePanel,
	useSearchUsers,
	buildFiltersPayload,
	getSidePanelTitle,
	getSidePanelMode,
} from "./hooks";
import { getCustomLabelApply } from "./utils";

const DeliverablesList = () => {
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		document.title = translate("navigation:project.deliverables");
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const { filters, searchTerm, handleApplyFilters: applyFilters, filtersPayload } = useDeliverablesFilters();
	const {
		projectId,
		rows,
		total,
		page,
		limit,
		isLoading,
		handleSort,
		fetchRows,
		getPreviewCount: baseGetPreviewCount,
		handleDeleteRow,
		handlePageChange,
		handleRowsPerPageChange,
		resetPage,
	} = useDeliverablesData(filtersPayload);
	const { dialogOptions, loadDialogOptions, handleExpandCategoryRow } = useDialogOptions(projectId, isMountedRef);
	const {
		isSidePanelOpen,
		sidePanelMode,
		selectedDeliverable,
		setSelectedDeliverable,
		openSidePanel: openPanel,
		handleCloseSidePanel,
		handleCreateDeliverableSubmit: createDeliverable,
		handleUpdateDeliverableSubmit,
	} = useSidePanel(isMountedRef, fetchRows);
	const searchUsersRequest = useSearchUsers(projectId);
	const handleApplyFilters = useCallback(
		(newFilters) => {
			applyFilters(newFilters);
			resetPage();
		},
		[applyFilters, resetPage]
	);
	const getPreviewCount = useCallback(
		(tempFilters) => {
			const tempSearch = (tempFilters?.content || "").trim();
			const tempPhases = tempFilters?.phases || {};
			const tempPhaseIds = tempPhases?.elements ?? tempPhases?.PHASE ?? [];
			const tempCategories = tempFilters?.categories || {};
			const tempCategoryIds = tempCategories?.CATEGORY ?? tempCategories?.elements ?? [];
			const tempOwners = tempFilters?.owners;
			let tempOwnerIds = [];

			if (Array.isArray(tempOwners)) {
				tempOwnerIds = tempOwners;
			} else if (Array.isArray(tempOwners?.elements)) {
				tempOwnerIds = tempOwners.elements;
			}

			const tempFiltersPayload = buildFiltersPayload({
				searchTerm: tempSearch,
				categoriesFilter: tempCategories,
				categoryIds: tempCategoryIds,
				phasesFilter: tempPhases,
				phaseIds: tempPhaseIds,
				ownerIds: tempOwnerIds,
			});

			return baseGetPreviewCount(tempFiltersPayload);
		},
		[baseGetPreviewCount]
	);
	const openSidePanel = useCallback(
		({ mode, deliverable = null, shouldLoadDialogOptions = false } = {}) => {
			openPanel({
				mode,
				deliverable,
				loadDialogOptionsCallback: shouldLoadDialogOptions
					? () => loadDialogOptions(mode, deliverable, setSelectedDeliverable)
					: null,
			});
		},
		[openPanel, loadDialogOptions, setSelectedDeliverable]
	);
	const handleCreateDeliverableSubmit = useCallback(
		(payload) => createDeliverable(projectId, payload),
		[createDeliverable, projectId]
	);
	const getSidePanelAction = () => {
		if (sidePanelMode === "edit" || sidePanelMode === "view") {
			return handleUpdateDeliverableSubmit;
		}
		return handleCreateDeliverableSubmit;
	};
	const getSidePanelDocument = () => {
		if (sidePanelMode === "edit" || sidePanelMode === "view") {
			return selectedDeliverable;
		}
		return null;
	};
	return (
		<>
			<HeaderSection />
			<FiltersBar
				filters={filters}
				getPreviewCount={getPreviewCount}
				searchTerm={searchTerm}
				searchUsersRequest={searchUsersRequest}
				onApplyFilters={handleApplyFilters}
				onOpenCreate={() => openSidePanel({ mode: "create", shouldLoadDialogOptions: true })}
				onOpenImport={() => openSidePanel({ mode: "import" })}
			/>
			<TableSection
				isLoading={isLoading}
				limit={limit}
				page={page}
				rows={rows}
				total={total}
				onDeleteRow={handleDeleteRow}
				onOpenCategories={(doc) => openSidePanel({ mode: "view", deliverable: doc })}
				onOpenEdit={(doc) => openSidePanel({ mode: "edit", deliverable: doc, shouldLoadDialogOptions: true })}
				onPageChange={handlePageChange}
				onRowsPerPageChange={handleRowsPerPageChange}
				onSort={handleSort}
			/>
			<SidePanel open={isSidePanelOpen} size={50} onClose={handleCloseSidePanel}>
				<SidePanelContent
					title={getSidePanelTitle(sidePanelMode, selectedDeliverable)}
					onClose={handleCloseSidePanel}
				>
					<DeliverableSidePanel
						action={getSidePanelAction()}
						document={getSidePanelDocument()}
						fbs={dialogOptions.fbs}
						formats={dialogOptions.formats}
						mode={getSidePanelMode(sidePanelMode)}
						owners={dialogOptions.owners}
						phases={dialogOptions.phases}
						projectId={projectId}
						types={dialogOptions.types}
						onClose={handleCloseSidePanel}
						onExpand={handleExpandCategoryRow}
						onImportSuccess={fetchRows}
					/>
				</SidePanelContent>
			</SidePanel>
		</>
	);
};
const HeaderSection = () => (
	<div className={styles.headerSection}>
		<div className={styles.titleSection}>
			<div className={styles.pageTitle}>{translate("deliverables-list.page-title")}</div>
			<div className={styles.subtitle}>{translate("deliverables-list.subtitle")}</div>
		</div>
	</div>
);
const FiltersBar = ({
	filters,
	searchTerm,
	searchUsersRequest,
	getPreviewCount,
	onApplyFilters,
	onOpenImport,
	onOpenCreate,
}) => (
	<div className={styles.filtersBar}>
		<div className={styles.filtersGroup}>
			<FiltersWrapper
				hasSearch
				components={[
					{
						default: true,
						enabled: true,
						component: CommonFilters.PHASES,
						renderer: (
							<ProjectPhaseFilter
								search={searchTerm}
								value={filters?.phases || {}}
								onChange={(v) => {
									onApplyFilters({
										...filters,
										phases: v,
									});
								}}
							/>
						),
					},
					{
						default: true,
						enabled: true,
						component: CommonFilters.CATEGORIES,
						renderer: (
							<ProjectCategoriesFilter
								disableFavoriteCategories
								search={searchTerm}
								value={filters}
								onChange={onApplyFilters}
							/>
						),
					},
					{
						default: true,
						enabled: true,
						component: CommonFilters.OWNERS,
						renderer: <ListWithRequest labelName="displayName" />,
						request: searchUsersRequest,
					},
				]}
				customLabelApply={getCustomLabelApply}
				defaultFilters={filters}
				previewRequest={getPreviewCount}
				onApply={onApplyFilters}
				onRemove={onApplyFilters}
			/>
		</div>
		<div className={styles.headerButtons}>
			<CustomButton startIcon={icon.faPlus} variant="outlined" onClick={onOpenImport}>
				{translate("deliverables-list.import_button")}
			</CustomButton>
			<CustomButton startIcon={icon.faPlus} variant="contained" onClick={onOpenCreate}>
				{translate("deliverables-list.button.create_deliverable")}
			</CustomButton>
		</div>
	</div>
);
const TableSection = ({
	isLoading,
	rows,
	total,
	page,
	limit,
	onSort,
	onDeleteRow,
	onOpenCategories,
	onOpenEdit,
	onPageChange,
	onRowsPerPageChange,
}) => (
	<div className={styles.tableWrapper}>
		{isLoading ? (
			<div className={styles.loaderContainer}>
				<CircularLoader />
			</div>
		) : (
			<CustomTable stickyHeader>
				<CustomTableHead>
					<HeaderDeliverableRow hasEditingRow={rows.some((row) => row.isEditing)} onSort={onSort} />
				</CustomTableHead>
				<TableBody>
					{rows.length > 0 ? (
						rows.map((row) => (
							<DeliverableRow
								key={row.id}
								deliverable={row}
								onDelete={onDeleteRow}
								onOpenCategories={onOpenCategories}
								onOpenEdit={onOpenEdit}
							/>
						))
					) : (
						<CustomTableRow>
							<CustomTableCell align="center" colSpan={8}>
								{translate("deliverables-list.no_deliverables")}
							</CustomTableCell>
						</CustomTableRow>
					)}
				</TableBody>
			</CustomTable>
		)}
		{total > 50 && (
			<TablePagination
				classes={{ root: styles.table__pagination }}
				component="div"
				count={total}
				page={page}
				rowsPerPage={limit}
				rowsPerPageOptions={[50, 100]}
				onPageChange={onPageChange}
				onRowsPerPageChange={onRowsPerPageChange}
			/>
		)}
	</div>
);

export default exportView({
	path: "/projects/:projectId/deliverables/list",
	localesPath: "/deliverables-list/locales",
	component: DeliverablesList,
});
